import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    Attachment,
    BodyAndAttachments,
    EnterprisePortalApi,
    Mail,
    MailFolderEnum,
    WebmailApi
} from "components/src/api/portal/generated";
import {AccessibleSpinner} from "components/src/common/AccessibleSpinner";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {Download} from "components/src/common/Download";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {Truncate} from "components/src/common/Truncate";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from 'dompurify';
import {
    ArrowLeft as BackIcon,
    CloudDownload as DownloadIcon,
    Reply as ReplyIcon,
    Trash as DeleteIcon
} from "iconoir-react";
import {useEffect, useState} from "react";
import {Dropdown} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ReplyPopup} from "./ReplyPopup";

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());
const enterprisePortalApi = new EnterprisePortalApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface ViewMailProps {
    mail : Mail
    onClose : OnClose
}

export const ViewMailPopup = ({mail, onClose} : ViewMailProps) => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The HTML bodies of the message
     */
    const [bodyAndAttachments, setBodyAndAttachments] = useState<BodyAndAttachments | undefined>(undefined);
    /**
     * If true, the "confirm delete" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * If true, a Download component will be loaded which will download the binary property
     */
    const [download, setDownload] = useState(false);
    /**
     * The blob containing the binary file to download
     */
    const [blob, setBlob] = useState<Blob>();
    /**
     * The filename for the downloaded file.
     */
    const [filename, setFilename] = useState<string>();
    /**
     * If true, the reply popup will be shown
     */
    const [showReplyPopup, setShowReplyPopup] = useState<boolean>(false);
    /**
     * true if a reply is allowed
     */
    const [isReplyAllowed, setIsReplyAllowed] = useState<boolean>(false);
    /**
     * true if a reply to the sender is allowed
     */
    const [isRecipientAllowed, setIsRecipientAllowed] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerGetBodyAndAttachments(mail.folder, mail.uid)
            .then((response) => {
                setBodyAndAttachments(response.data);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [mail.folder,
        mail.uid,
        navigate,
        setSpinnerStateCounter,
        t]);

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        enterprisePortalApi.enterprisePortalControllerGetEnterprisePortalProperties()
            .then(response => {
                setIsReplyAllowed(response.data.webmailIsReplyAllowed);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        t]);

    useEffect(() => {
        if (mail.from === undefined) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerIsRecipientAllowed(mail.from)
            .then(response => {
                setIsRecipientAllowed(response.data);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        t,
        mail.from]);

    const deleteMessage = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerDeleteMessages(mail.folder, [mail.uid])
            .then(() => {
                closePage(DialogCloseCause.Succes);
                setShowDeleteConfirm(false);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }

    const downloadAttachment = (attachment: Attachment) =>
    {
        setFilename(attachment.filename);

        webmailApi.webmailPortalControllerGetAttachment(mail.folder, mail.uid, attachment.index,
            {responseType: 'blob'}).then((response) => {
            setBlob(new Blob([response.data], {type: "application/octet-stream"}));

            // enable the Download component
            setDownload(true);
        })
        .catch((err: Error | AxiosError) => {
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
    }

    const downloadMessage = () =>
    {
        setFilename(mail.subject + ".eml");

        webmailApi.webmailPortalControllerGetMimeMessage(mail.folder, mail.uid).then((response) =>
        {
            setBlob(new Blob([response.data as unknown as string], {type: "message/rfc822"}));

            // enable the Download component
            setDownload(true);
        })
        .catch((err: Error | AxiosError) => {
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
    }

    /**
     * Is called when the download started. This should reset download to make sure the Download component is no
     * longer added.
     */
    const onDownload = () => {
        // disable the Download component
        setDownload(false);
    }

    const sanitizeHTML = (html: string) => {
        return {__html: DOMPurify.sanitize(html)};
    }

    const reply = () => {
        setShowReplyPopup(true);
    }

    const hideReplyPopup = () => {
        setShowReplyPopup(false);
    }

    const closePage = (dialogCloseCause : DialogCloseCause) => {
        onClose(dialogCloseCause);
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("portal.messages.confirm-delete-message")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={() => deleteMessage()}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showReplyPopup && <ReplyPopup sourceMail={mail} sourceBody={bodyAndAttachments?.text}
                                           onClose={hideReplyPopup}/>}

            <div>
                <Modal show={true}
                       onHide={() => closePage(DialogCloseCause.Cancel)}
                       backdrop="static"
                       scrollable={true}
                       dialogClassName={"cm-modal-dialog"}
                       contentClassName={"cm-modal-content"}
                >
                    <Modal.Header className="d-inline pb-2">
                        <Row className="mb-2">
                            <Col className="d-flex flex-row">
                                <div className="d-flex justify-content-start align-items-center">
                                    <BackIcon onClick={() => closePage(DialogCloseCause.Cancel)} className="text-secondary cursor-pointer"/>
                                    <h1 className="h3 ms-2 mt-0 mb-0 text-muted">{mail.subject}</h1>
                                </div>
                                <div className="d-flex flex-fill justify-content-end">
                                    <AccessibleSpinner show={spinnerStateCounter > 0}/>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-2">
                            <Col>
                                <div className="fs-5 text-success fw-bold">{StringUtils.unquoteEmail(mail.from)}</div>
                            </Col>
                        </Row>

                        <Row className="mb-2">
                            <Col>
                                <span className="fs-5 text-secondary">{t('portal.messages.to')}: </span>
                                <span className="fs-5 text-secondary"><Truncate value={mail.to?.map(e => StringUtils.unquoteEmail(e)).join(", ")} maxLength={120}/></span>
                            </Col>
                        </Row>

                        {mail.cc && mail.cc.length > 0 &&
                        <Row className="mb-2">
                            <Col>
                                <span className="fs-5 text-secondary">{t('portal.messages.cc')}: </span>
                                <span className="fs-5 text-secondary"><Truncate value={mail.cc?.map(e => StringUtils.unquoteEmail(e)).join(", ")} maxLength={120}/></span>
                            </Col>
                        </Row>
                        }

                        <Row className="mb-2">
                            <Col className={"d-flex align-items-center"}>
                                <div className="d-flex justify-content-start">
                                    <Button variant="primary"
                                            size={"sm"}
                                            className="d-flex align-items-center"
                                            onClick={() => reply()}
                                            disabled={
                                                !isReplyAllowed ||
                                                !isRecipientAllowed ||
                                                mail.folder != MailFolderEnum.Inbox ||
                                                StringUtils.isBlank(mail.from)}>
                                        <ReplyIcon height="1em" width="1em" className="me-1"/> {t("portal.messages.reply")}
                                    </Button>

                                    <Button variant="danger"
                                            size={"sm"}
                                            className="d-flex align-items-center ms-2"
                                            onClick={() => setShowDeleteConfirm(true)}>
                                        <DeleteIcon height="1em" width="1em" className="me-1"/> {t("portal.messages.delete")}
                                    </Button>
                                </div>

                                <div className="d-flex flex-fill justify-content-end align-items-center">
                                    <ActionsDropdown size={"sm"}>
                                        <Dropdown.Item onClick={() => downloadMessage()}>
                                            <DownloadIcon className="me-2"/> {t("portal.messages.download-message")}
                                        </Dropdown.Item>
                                    </ActionsDropdown>
                                </div>
                            </Col>
                        </Row>

                        {bodyAndAttachments !== undefined && bodyAndAttachments.attachments.length > 0 &&
                            <Row className="mt-2">
                                <Col>
                                    <span className="text-secondary">{t('portal.messages.attachment-count', {count : bodyAndAttachments.attachments.length})}</span>
                                </Col>
                            </Row>
                        }

                        <Row className="overflow-auto">
                            <Col className="d-flex flex-row">
                                {bodyAndAttachments?.attachments.map((attachment) => (
                                    // make the div focusable and execute when the enter key is pressed
                                    <div key={attachment.index}
                                         className="text-secondary me-2 d-flex align-items-center"
                                         title={t("portal.messages.download-attachment")}
                                         onClick={() => downloadAttachment(attachment)}
                                         role="button"
                                         tabIndex={0}
                                         onKeyDown={(e) => Utils.clickOnEnterKeyPressed(e)}>

                                        <Truncate className="text-nowrap" value={attachment.filename} maxLength={30}/>
                                        <div className="ms-1">
                                            <DownloadIcon height="1em" width="1em" className="text-primary"/>
                                        </div>
                                    </div>
                                ))}
                            </Col>
                        </Row>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row flex-grow-1">
                            <main className="col d-flex flex-column overflow-auto modal-main-body">

                                <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                             tooltipPlacement={"bottom"}/>

                                <Row className="h-100 w-100 overflow-auto">
                                    <Col>
                                        {bodyAndAttachments?.html.map((htmlBody, index) => (
                                            <div key={index} dangerouslySetInnerHTML={sanitizeHTML(htmlBody)}/>
                                        ))}
                                    </Col>
                                </Row>
                            </main>
                        </div>
                    </Modal.Body>

                    {download && blob !== undefined && (
                        <Download blob={blob}
                                  filename={filename??"unknown"}
                                  onDownload={onDownload}/>
                    )}
                </Modal>
            </div>
        </>
    );
}
